import i18n from '@/utils/i18n';
import React, { useCallback, useRef, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import { ActivityIndicator } from './activity-indicator';
import { AllowedShortcuts } from '@/platform/shortcuts/operator-shortcuts';
import {
  TextInput as BaseTextInput,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import {
  handleWebKeyPress,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import { TextInput } from './text-input';
import { useDebouncedCallback } from 'use-debounce';
import { useOperatorKeyboardShortcuts } from '@/platform/shortcuts/hooks/use-operator-keyboard-shortcuts';

const DEBOUNCE_DELAY_MS = 500;

interface Props {
  autoFocus?: boolean;
  containerStyle?: StyleProp<ViewStyle> | null;
  editable?: boolean;
  initialSearchText?: string | null;
  isLoading?: boolean;
  onFocus?: () => void;
  onPress?: () => void;
  onSubmit: (value: string | null) => void;
  placeholder?: string;
  submitOnChange?: boolean;
}

const SEARCH_BUTTON_HIT_SLOP = {
  bottom: 12,
  left: 12,
  right: 12,
  top: 12,
};

export const ItemSearchHeaderAction: React.FC<Props> = ({
  editable,
  isLoading,
  containerStyle,
  initialSearchText,
  onSubmit,
  placeholder,
  submitOnChange,
  onFocus,
  onPress,
  autoFocus,
}) => {
  const [styles, theme] = useStyles();

  const [searchText, setSearchText] = useState(initialSearchText || '');
  const [selection, setSelection] = useState({ end: 0, start: 0 });
  const inputRef = useRef<BaseTextInput>(null);

  const debouncedSubmit = useDebouncedCallback((text: string) => {
    onSubmit(text);
  }, DEBOUNCE_DELAY_MS);

  useOperatorKeyboardShortcuts(
    AllowedShortcuts.Search,
    () => {
      // If onPress is provided and input is not editable, call onPress instead of focusing
      if (onPress && editable === false) {
        onPress();
      } else if (inputRef.current) {
        inputRef.current.focus();
        // Set the selection to highlight all text
        setSelection({ end: searchText.length, start: 0 });
      }
    },
    [searchText, onPress, editable],
  );

  useComponentFocus({
    onBlur: useCallback(
      (focusSource: FocusSource) => {
        if (focusSource === FocusSource.Navigation) {
          // Clear any search state when navigating away
          debouncedSubmit.cancel();
          setSearchText('');
        }
      },
      [debouncedSubmit],
    ),
    onFocus: useCallback(
      (focusSource: FocusSource) => {
        // Restore search text when component regains focus
        if (focusSource === FocusSource.Navigation && initialSearchText) {
          setSearchText(initialSearchText);
        }
      },
      [initialSearchText],
    ),
  });

  return (
    <TextInput
      autoFocus={autoFocus}
      clearButtonMode="while-editing"
      containerStyle={[styles.textInputContainer, containerStyle]}
      editable={editable}
      inputRef={inputRef}
      onChangeText={(text) => {
        setSearchText(text);
        if (text === '') {
          debouncedSubmit.cancel();
          onSubmit(null);
        } else if (submitOnChange) {
          onSubmit(text);
        } else {
          debouncedSubmit(text);
        }
      }}
      onFocus={onFocus}
      onKeyPress={(e) => {
        handleWebKeyPress(e);

        switch (e.nativeEvent.key) {
          case Key.Enter:
            debouncedSubmit.cancel();
            onSubmit(searchText);
            break;
          case Key.Escape:
            inputRef.current?.blur();
            break;
        }
      }}
      onPress={onPress}
      onSelectionChange={({ nativeEvent: { selection } }) =>
        setSelection(selection)
      }
      onSubmitEditing={() => {
        debouncedSubmit.cancel();
        onSubmit(searchText);
      }}
      placeholder={
        typeof placeholder === 'string' ? placeholder : i18n.t('search')
      }
      rightElement={
        <View style={styles.rightElementContainer}>
          {isLoading ? (
            <ActivityIndicator size="small" />
          ) : (
            <TouchableOpacity
              hitSlop={SEARCH_BUTTON_HIT_SLOP}
              onPress={() => {
                debouncedSubmit.cancel();
                onSubmit(searchText);
              }}
              testID="item-search-header-button"
            >
              <SearchIcon fill={theme.colors.primary} />
            </TouchableOpacity>
          )}
        </View>
      }
      rightElementBordered
      selection={selection}
      testID="item-search-header-input"
      value={searchText || ''}
    />
  );
};

const useStyles = createStyles((theme) => ({
  rightElementContainer: {
    width: 24,
  },
  textInputContainer: {
    backgroundColor: theme.colors.background,
    width: 360,
  },
}));
